import { useEffect } from 'react';

import { useFormContext } from 'react-hook-form';

import { createTaxReturnRequest } from './creatorTaxReturnRequest';
import type {
  CellChanged,
  FormDefinitionLike,
  FormulaFailed,
  TaxReturnCalculationConnection,
  TaxReturnRequestRuntime
} from './types';

const calculateTaxReturnMethod = 'CalculateTaxReturn';
const cellChangedEvent = 'CellChanged';
const formulaFailedEvent = 'FormulaFailed';
const connectedState = 'Connected';

interface TaxReturnChangeComponentProps<
  TRequest,
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod extends object = object
> {
  value: unknown;
  connection?: TaxReturnCalculationConnection;
  runtime: TaxReturnRequestRuntime<TDefinition, TPeriod>;
  addLogRule?: (message: string) => void;
}

export const TaxReturnChangeComponent = <
  TRequest,
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod extends object = object
>({
  value,
  connection,
  runtime,
  addLogRule
}: TaxReturnChangeComponentProps<TRequest, TDefinition, TPeriod>) => {
  const { getValues } = useFormContext();
  const { def, formVersionId, period, taxpayerId, amendedTaxReturnId } = runtime;

  useEffect(() => {
    if (value === undefined) return;
    if (!connection || connection.state !== connectedState) return;

    const taxReturnRequest = createTaxReturnRequest<TRequest>({
      formVersionId,
      def,
      form: getValues(),
      taxpayerId,
      period,
      amendedTaxReturnId
    });

    addLogRule?.('Executing....');
    connection.send(calculateTaxReturnMethod, taxReturnRequest).catch((error) => {
      addLogRule?.(`SendFailed: ${String(error?.message ?? error)}`);
    });
  }, [
    value,
    connection,
    getValues,
    addLogRule,
    amendedTaxReturnId,
    def,
    formVersionId,
    period,
    taxpayerId
  ]);

  return <></>;
};

interface TaxReturnInitializerProps<
  TRequest,
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod extends object = object
> {
  readOnly: boolean;
  connection?: TaxReturnCalculationConnection;
  runtime: TaxReturnRequestRuntime<TDefinition, TPeriod>;
}

export const TaxReturnInitializer = <
  TRequest,
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod extends object = object
>({
  readOnly,
  connection,
  runtime
}: TaxReturnInitializerProps<TRequest, TDefinition, TPeriod>) => {
  const { getValues } = useFormContext();
  const { def, formVersionId, period, taxpayerId, amendedTaxReturnId } = runtime;

  useEffect(() => {
    if (readOnly) return;

    const taxReturnRequest = createTaxReturnRequest<TRequest>({
      formVersionId,
      def,
      form: getValues(),
      taxpayerId,
      period,
      amendedTaxReturnId
    });

    connection?.send(calculateTaxReturnMethod, taxReturnRequest);
  }, [readOnly, connection, getValues, amendedTaxReturnId, def, formVersionId, period, taxpayerId]);

  return <></>;
};

interface TaxReturnCellChangedHandlerProps<TCellChanged extends CellChanged = CellChanged> {
  connection?: TaxReturnCalculationConnection<TCellChanged>;
  addLogRule?: (message: string) => void;
  onChange?: (cell: TCellChanged['cell']) => void;
}

export const TaxReturnCellChangedHandler = <TCellChanged extends CellChanged = CellChanged>({
  connection,
  addLogRule,
  onChange
}: TaxReturnCellChangedHandlerProps<TCellChanged>) => {
  const { getValues, setValue } = useFormContext();

  useEffect(() => {
    if (!connection) return;

    const handler = ({ cell }: TCellChanged) => {
      onChange?.(cell);
      const value = cell.type === 'currency' ? Number(cell.value).toFixed(2) : cell.value;

      addLogRule?.(`C${cell?.code}  update with ${value}`);
      const path = resolveSectionCellPath(getValues(), cell.code, cell.sectionRowId);
      setValue(path ?? cell.code.toString(), value);
    };

    connection.on(cellChangedEvent, handler);

    return () => {
      connection.off(cellChangedEvent, handler);
    };
  }, [connection, addLogRule, getValues, onChange, setValue]);

  return <></>;
};

export const resolveSectionCellPath = (form: any, code: number, sectionRowId?: string | null) => {
  for (const [candidateSectionCode, section] of Object.entries<any>(form?.sections ?? {})) {
    const index = (section.instances ?? []).findIndex(
      (instance: any) =>
        instance.sectionRowId === sectionRowId && code.toString() in (instance.cells ?? {})
    );
    if (index >= 0) return `sections.${candidateSectionCode}.instances.${index}.cells.${code}`;
  }

  return undefined;
};

interface TaxReturnFormulaFailedHandlerProps<TFormulaFailed extends FormulaFailed = FormulaFailed> {
  connection?: TaxReturnCalculationConnection<CellChanged, TFormulaFailed>;
  addLogRule?: (message: string) => void;
}

export const TaxReturnFormulaFailedHandler = <
  TFormulaFailed extends FormulaFailed = FormulaFailed
>({
  connection,
  addLogRule
}: TaxReturnFormulaFailedHandlerProps<TFormulaFailed>) => {
  useEffect(() => {
    if (!connection) return;

    const handler = (error: TFormulaFailed) => {
      addLogRule?.('------------------');
      addLogRule?.(`FormulaFailed | C${error.taxReturnCell.code}`);
      addLogRule?.(error.exceptionMessage);
      addLogRule?.('------------------');
    };

    connection.on(formulaFailedEvent, handler);

    return () => {
      connection.off(formulaFailedEvent, handler);
    };
  }, [connection, addLogRule]);

  return <></>;
};
